import React, { useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import type {
  Site,
  SiteContact,
  SiteContactInput,
} from '@core/generated/gql-types';
import { useSiteStore } from '@core/site/useSiteStore';
import Button from '@/components/Button';

interface SiteContactCardProps {
  site: Site;
  index: 0 | 1;
  title: 'Contact 1' | 'Contact 2';
}

interface SiteContactFormValues {
  name: string;
  email: string;
  phone: string;
}

const contactValidationSchema = Yup.object({
  name: Yup.string(),
  email: Yup.string()
    .transform((value) => (value?.trim() ? value.trim() : undefined))
    .email('Enter a valid email address')
    .optional(),
  phone: Yup.string()
    .transform((value) => (value?.trim() ? value.trim() : undefined))
    .matches(/^[+\d\s()-]+$/, 'Phone number cannot contain letters')
    .optional(),
});

const toFormValues = (
  contact: SiteContact | undefined,
): SiteContactFormValues => ({
  name: contact?.name ?? '',
  email: contact?.email ?? '',
  phone: contact?.phone ?? '',
});

const normalizeContact = (
  contact: SiteContact | SiteContactFormValues,
): SiteContactInput | null => {
  const normalized = {
    name: contact.name?.trim() || undefined,
    email: contact.email?.trim() || undefined,
    phone: contact.phone?.trim() || undefined,
  };

  return normalized.name || normalized.email || normalized.phone
    ? normalized
    : null;
};

const SiteContactCard: React.FC<SiteContactCardProps> = ({
  site,
  index,
  title,
}) => {
  const { updateSite } = useSiteStore();
  const [isEditing, setIsEditing] = useState(false);
  const contact = site.contacts?.[index];

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        {!isEditing && (
          <Button
            type="button"
            variant="text"
            onClick={() => setIsEditing(true)}
            className="gap-2 px-0 py-0 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />
            Edit
          </Button>
        )}
      </div>

      {isEditing ? (
        <Formik<SiteContactFormValues>
          enableReinitialize
          initialValues={toFormValues(contact)}
          validationSchema={contactValidationSchema}
          onSubmit={async (values, { setStatus }) => {
            setStatus(undefined);

            const nextContacts = [...(site.contacts ?? [])];
            nextContacts[index] = values;

            const contacts = nextContacts
              .map(normalizeContact)
              .filter(
                (nextContact): nextContact is SiteContactInput =>
                  nextContact !== null,
              )
              .slice(0, 2);

            const result = await updateSite(site.id, { contacts });
            if (!result.success) {
              setStatus(result.error ?? 'Failed to update contact');
              return;
            }

            setIsEditing(false);
          }}
        >
          {({ isSubmitting, status, resetForm }) => (
            <Form className="space-y-4 p-6">
              {status && (
                <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                  {status}
                </div>
              )}

              {(
                [
                  ['name', 'Name', 'Name'],
                  ['email', 'Email', 'Email'],
                  ['phone', 'Phone', 'Phone'],
                ] as const
              ).map(([name, label, placeholder]) => (
                <div key={name}>
                  <label
                    htmlFor={`${site.id}-${index}-${name}`}
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    {label}
                  </label>
                  <Field
                    id={`${site.id}-${index}-${name}`}
                    name={name}
                    type={name === 'email' ? 'email' : 'text'}
                    placeholder={placeholder}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                  <ErrorMessage
                    name={name}
                    component="div"
                    className="mt-1 text-xs text-red-500"
                  />
                </div>
              ))}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => {
                    resetForm();
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 text-sm font-medium"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-medium"
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        <dl className="divide-y divide-gray-100">
          {[
            ['Name', contact?.name],
            ['Email Address', contact?.email],
            ['Phone', contact?.phone],
          ].map(([label, value]) => (
            <div
              key={label}
              className="flex items-start justify-between px-6 py-4"
            >
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="max-w-[65%] text-right text-sm font-medium break-words text-gray-900">
                {value || '-'}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

export default SiteContactCard;
